"use client"
import { Edit2, CheckCircle, XCircle, Trash2 } from "lucide-react"
import { Button } from "./ui/Button"

/**
 * Table component for displaying cost codes
 * @param {Object} props - Component props
 * @param {Array} props.codes - Cost codes data
 * @param {Function} props.onEdit - Edit callback
 * @param {Function} props.onToggle - Toggle active callback
 * @param {Object} props.pagination - Pagination state
 * @param {boolean} props.loading - Loading state
 */
/**
 * Table component for displaying cost codes
 * @param {Object} props - Component props
 * @param {Array} props.codes - Cost codes data
 * @param {Function} props.onEdit - Edit callback
 * @param {Function} props.onToggle - Toggle active callback
 * @param {Function} props.onDelete - Delete callback for unused codes
 * @param {Object} props.pagination - Pagination state
 * @param {boolean} props.loading - Loading state
 */
const CostCodesTable = ({ codes, onEdit, onToggle, onDelete, pagination = {}, loading }) => {
  const { page, totalPages, onPrevPage, onNextPage } = pagination;

  if (loading) {
    return (
      <div className="w-full text-center py-8">
        <div className="animate-pulse text-gray-400">Loading cost codes...</div>
      </div>
    )
  }

  if (!codes.length) {
    return (
      <div className="w-full text-center py-8 bg-gray-800 rounded-lg border border-gray-700">
        <p className="text-gray-400">No cost codes found</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm text-left text-white">
          <thead className="text-xs uppercase bg-gray-800 text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3">
                Code
              </th>
              <th scope="col" className="px-6 py-3">
                Description
              </th>
              <th scope="col" className="px-6 py-3">
                Category
              </th>
              {/* Tags column removed */}
              <th scope="col" className="px-6 py-3">
                Status
              </th>
              <th scope="col" className="px-6 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr
                key={code._id}
                className="odd:bg-gray-900 even:bg-gray-850 border-b border-gray-700 hover:bg-gray-800"
              >
                <td className="px-6 py-4 font-medium whitespace-nowrap">{code.code}</td>
                <td className="px-6 py-4">{code.description}</td>
                <td className="px-6 py-4">{code.category}</td>
                {/* Tags removed */}
                <td className="px-6 py-4">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    code.active ? "bg-green-900 text-green-300" : "bg-red-900 text-red-300"
                  } transition-colors duration-200`}
                >
                  {code.active ? "Active" : "Inactive"}
                </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
                      onClick={() => onEdit(code)}
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                    { onDelete && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-red-600 border-red-700 hover:bg-red-700 text-white"
                        onClick={() => onDelete(code)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className={`${
                        code.active
                          ? "bg-red-600 hover:bg-red-700 border-red-700"
                          : "bg-green-600 hover:bg-green-700 border-green-700"
                      } text-white transition-colors duration-200`}
                      onClick={() => onToggle(code)}
                    >
                      {code.active ? (
                        <>
                          <XCircle className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Deactivate</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Activate</span>
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (if provided) */}
      {page != null && totalPages != null && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-400">
            Page {page} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevPage}
              disabled={page <= 1}
              className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={page >= totalPages}
              className="bg-gray-800 border-gray-700 hover:bg-gray-700 text-white"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CostCodesTable
